import React from 'react';
import { Filter, Euro, Calendar, Users, Building, X } from 'lucide-react';

const ControlPanel = ({ 
  selectedSellerLeague, 
  setSelectedSellerLeague, 
  selectedBuyerLeague, 
  setSelectedBuyerLeague, 
  minAmount, 
  setMinAmount, 
  maxAmount, 
  setMaxAmount,
  selectedPosition,
  setSelectedPosition,
  selectedTransferType,
  setSelectedTransferType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  excludeOtherLeagues,
  setExcludeOtherLeagues,
  minPercentile,
  setMinPercentile,
  maxPercentile,
  setMaxPercentile
}) => {
  const leagues = ['All', 'Premier League', 'La Liga', 'Bundesliga', 'Serie A', 'Ligue 1'];
  const positions = ['All', 'Goalkeeper', 'Defender', 'Midfielder', 'Forward'];
  const transferTypes = ['All', 'Free', 'Loan', 'Paid'];

  const formatAmount = (amount) => {
    if (amount === 0) return '€0';
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(0)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  // Handle zero values properly for logarithmic scale
  const logToLinear = (logValue) => {
    if (logValue <= 0) return 0;
    return Math.pow(10, logValue);
  };

  const linearToLog = (linearValue) => {
    if (linearValue <= 0) return 0;
    return Math.log10(linearValue);
  };

  // Convert current values to logarithmic scale, handling zero properly
  const minLog = minAmount === 0 ? 0 : linearToLog(minAmount);
  const maxLog = maxAmount === 0 ? 0 : linearToLog(maxAmount);

  const handleMinChange = (e) => {
    const logValue = parseFloat(e.target.value);
    const linearValue = logValue === 0 ? 0 : Math.round(logToLinear(logValue));
    setMinAmount(linearValue);
    
    // Ensure min doesn't exceed max
    if (linearValue > maxAmount) {
      setMaxAmount(linearValue);
    }
  };

  const handleMaxChange = (e) => {
    const logValue = parseFloat(e.target.value);
    const linearValue = logValue === 0 ? 0 : Math.round(logToLinear(logValue));
    setMaxAmount(linearValue);
    
    // Ensure max doesn't go below min
    if (linearValue < minAmount) {
      setMinAmount(linearValue);
    }
  };

  // Handle percentile slider changes
  const handleMinPercentileChange = (e) => {
    const value = parseFloat(e.target.value);
    setMinPercentile(value);
    
    // Ensure min doesn't exceed max
    if (value > maxPercentile) {
      setMaxPercentile(value);
    }
  };

  const handleMaxPercentileChange = (e) => {
    const value = parseFloat(e.target.value);
    setMaxPercentile(value);
    
    // Ensure max doesn't go below min
    if (value < minPercentile) {
      setMinPercentile(value);
    }
  };

  // Handle click on slider track to move closest marker
  const handleSliderClick = (e) => {
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercent = (clickX / rect.width) * 100;
    
    // Convert percentage to logarithmic value
    const logValue = (clickPercent / 100) * 8.176;
    
    // Determine which marker is closer
    const distanceToMin = Math.abs(logValue - minLog);
    const distanceToMax = Math.abs(logValue - maxLog);
    
    if (distanceToMin <= distanceToMax) {
      // Move min marker
      const linearValue = logValue === 0 ? 0 : Math.round(logToLinear(logValue));
      setMinAmount(linearValue);
      
      // Ensure min doesn't exceed max
      if (linearValue > maxAmount) {
        setMaxAmount(linearValue);
      }
    } else {
      // Move max marker
      const linearValue = logValue === 0 ? 0 : Math.round(logToLinear(logValue));
      setMaxAmount(linearValue);
      
      // Ensure max doesn't go below min
      if (linearValue < minAmount) {
        setMinAmount(linearValue);
      }
    }
  };

  // Handle click on percentile slider track
  const handlePercentileSliderClick = (e) => {
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercent = (clickX / rect.width) * 100;
    
    // Determine which marker is closer
    const distanceToMin = Math.abs(clickPercent - minPercentile);
    const distanceToMax = Math.abs(clickPercent - maxPercentile);
    
    if (distanceToMin <= distanceToMax) {
      // Move min marker
      setMinPercentile(clickPercent);
      
      // Ensure min doesn't exceed max
      if (clickPercent > maxPercentile) {
        setMaxPercentile(clickPercent);
      }
    } else {
      // Move max marker
      setMaxPercentile(clickPercent);
      
      // Ensure max doesn't go below min
      if (clickPercent < minPercentile) {
        setMinPercentile(clickPercent);
      }
    }
  };

  // Values at every 20% of the slider range with alternating positions
  const significantValues = [
    { value: 0, label: '€0', position: 'below' },
    { value: Math.round(logToLinear(8.176 * 0.2)), label: formatAmount(Math.round(logToLinear(8.176 * 0.2))), position: 'above' },
    { value: Math.round(logToLinear(8.176 * 0.4)), label: formatAmount(Math.round(logToLinear(8.176 * 0.4))), position: 'below' },
    { value: Math.round(logToLinear(8.176 * 0.6)), label: formatAmount(Math.round(logToLinear(8.176 * 0.6))), position: 'above' },
    { value: Math.round(logToLinear(8.176 * 0.8)), label: formatAmount(Math.round(logToLinear(8.176 * 0.8))), position: 'below' },
    { value: 150000000, label: '€150M', position: 'above' }
  ];

  const getTickPosition = (value) => {
    if (value === 0) return 0;
    const logValue = linearToLog(value);
    return (logValue / 8.176) * 100; // 8.176 is log10(150M)
  };

  return (
    <div className="control-panel">
      <div className="filter-row">
        <label>From:</label>
        <select 
          value={selectedSellerLeague} 
          onChange={(e) => setSelectedSellerLeague(e.target.value)}
        >
          {leagues.map(league => (
            <option key={league} value={league}>{league}</option>
          ))}
        </select>
      </div>

      <div className="filter-row">
        <label>To:</label>
        <select 
          value={selectedBuyerLeague} 
          onChange={(e) => setSelectedBuyerLeague(e.target.value)}
        >
          {leagues.map(league => (
            <option key={league} value={league}>{league}</option>
          ))}
        </select>
      </div>

      <div className="filter-row">
        <label>Position:</label>
        <select 
          value={selectedPosition} 
          onChange={(e) => setSelectedPosition(e.target.value)}
        >
          {positions.map(position => (
            <option key={position} value={position}>{position}</option>
          ))}
        </select>
      </div>

      <div className="filter-row">
        <label>Type:</label>
        <select 
          value={selectedTransferType} 
          onChange={(e) => setSelectedTransferType(e.target.value)}
        >
          {transferTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="filter-row">
        <label>Date:</label>
        <div className="date-range">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />
          <span>-</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />
        </div>
      </div>

      <div className="filter-row">
        <label>Amount:</label>
        <div className="amount-range-dual">
          <div className="range-container">
            {/* Clickable slider track */}
            <div 
              className="slider-track-clickable"
              onClick={handleSliderClick}
            ></div>
            
            {/* Tick marks for significant values */}
            <div className="range-ticks">
              {significantValues.map((tick, index) => (
                <div 
                  key={index}
                  className={`range-tick range-tick-${tick.position}`}
                  style={{ left: `${getTickPosition(tick.value)}%` }}
                >
                  <div className="tick-mark"></div>
                  <div className="tick-label">{tick.label}</div>
                </div>
              ))}
            </div>
            
            <input
              type="range"
              min="0"
              max="8.176"
              step="0.01"
              value={minLog}
              onChange={handleMinChange}
              className="range-slider range-slider-min"
            />
            <input
              type="range"
              min="0"
              max="8.176"
              step="0.01"
              value={maxLog}
              onChange={handleMaxChange}
              className="range-slider range-slider-max"
            />
          </div>
          <div className="amount-display">
            {formatAmount(minAmount)} - {formatAmount(maxAmount)}
          </div>
        </div>
      </div>

      <div className="filter-row">
        <label>Top %:</label>
        <div className="percentile-range-dual">
          <div className="range-container">
            {/* Clickable percentile slider track */}
            <div 
              className="slider-track-clickable"
              onClick={handlePercentileSliderClick}
            ></div>
            
            {/* Tick marks for percentile values */}
            <div className="range-ticks">
              {[0, 25, 50, 75, 100].map((percent, index) => (
                <div 
                  key={index}
                  className={`range-tick range-tick-${index % 2 === 0 ? 'below' : 'above'}`}
                  style={{ left: `${percent}%` }}
                >
                  <div className="tick-mark"></div>
                  <div className="tick-label">{percent}%</div>
                </div>
              ))}
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={minPercentile}
              onChange={handleMinPercentileChange}
              className="range-slider range-slider-min"
            />
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={maxPercentile}
              onChange={handleMaxPercentileChange}
              className="range-slider range-slider-max"
            />
          </div>
          <div className="amount-display">
            {minPercentile.toFixed(0)}% - {maxPercentile.toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="filter-row compact-toggle">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={excludeOtherLeagues}
            onChange={(e) => setExcludeOtherLeagues(e.target.checked)}
            className="toggle-checkbox"
          />
          <span className="toggle-text">Major Leagues Only</span>
        </label>
      </div>
    </div>
  );
};

export default ControlPanel;
